import {
  isItemActive,
  type KitchenTicket,
  type KitchenTicketItem,
  type KitchenTicketItemStatus,
  type KitchenTicketStatus,
} from '@/types/kitchen';

export const NEXT_STATUS: Record<KitchenTicketStatus, KitchenTicketStatus | null> = {
  pending: 'preparing',
  preparing: 'ready',
  ready: 'delivered',
  delivered: null,
  cancelled: null,
};

export const PREVIOUS_STATUS: Record<KitchenTicketStatus, KitchenTicketStatus | null> = {
  pending: null,
  preparing: 'pending',
  ready: 'preparing',
  delivered: 'ready',
  cancelled: null,
};

type ItemForAggregate = Pick<KitchenTicketItem, 'status' | 'quantity' | 'cancelledQuantity'>;

/** Ticket-level status derived from active (non-fully-cancelled) items. */
export const deriveTicketStatus = (items: ItemForAggregate[]): KitchenTicketStatus => {
  const active = items.filter(isItemActive);
  if (active.length === 0) return 'pending';
  if (active.every((item) => item.status === 'delivered')) return 'delivered';
  if (active.every((item) => item.status === 'pending')) return 'pending';
  if (active.every((item) => item.status === 'ready' || item.status === 'delivered')) {
    return 'ready';
  }
  return 'preparing';
};

/** Whether a ticket still belongs on the KDS board. */
export const isKitchenTicketActiveOnBoard = (ticket: KitchenTicket): boolean => {
  if (ticket.status === 'delivered' || ticket.status === 'cancelled') return false;
  return deriveTicketStatus(ticket.items) !== 'delivered';
};

/** POS header: something on this ticket is ready to pick up, even if other items are still cooking. */
export const hasItemsReadyToServe = (ticket: KitchenTicket): boolean =>
  ticket.items.some((item) => isItemActive(item) && item.status === 'ready');

export const applyItemStatus = (
  item: KitchenTicketItem,
  status: KitchenTicketItemStatus,
  now: Date,
): KitchenTicketItem => {
  if (status === 'preparing') {
    return {
      ...item,
      status,
      startedAt: item.startedAt ?? now,
      readyAt: null,
      deliveredAt: null,
    };
  }

  if (status === 'ready') {
    return {
      ...item,
      status,
      readyAt: now,
      deliveredAt: null,
    };
  }

  if (status === 'delivered') {
    return {
      ...item,
      status,
      deliveredAt: now,
    };
  }

  // pending (revert from preparing) — keep timestamps so elapsed time is preserved
  return {
    ...item,
    status,
  };
};

export const applyTicketStatus = (
  ticket: KitchenTicket,
  status: KitchenTicketStatus,
  now: Date,
): KitchenTicket => {
  if (status === 'delivered') {
    return {
      ...ticket,
      status,
      deliveredAt: now,
      updatedAt: now,
    };
  }

  if (status === 'cancelled') {
    return {
      ...ticket,
      status,
      cancelledAt: now,
      updatedAt: now,
    };
  }

  if (status === 'preparing') {
    return {
      ...ticket,
      status,
      startedAt: ticket.startedAt ?? now,
      readyAt: null,
      deliveredAt: null,
      cancelledAt: null,
      updatedAt: now,
    };
  }

  if (status === 'ready') {
    return {
      ...ticket,
      status,
      readyAt: now,
      deliveredAt: null,
      cancelledAt: null,
      updatedAt: now,
    };
  }

  // pending (revert from preparing) — keep timestamps so elapsed time is preserved
  return {
    ...ticket,
    status,
    updatedAt: now,
  };
};

/** Cascades a ticket-level status onto every active item, then stamps the ticket. */
export const applyTicketStatusToAllActiveItems = (
  ticket: KitchenTicket,
  status: KitchenTicketStatus,
  now: Date,
): KitchenTicket => {
  if (status === 'cancelled') {
    return applyTicketStatus(ticket, status, now);
  }

  const items = ticket.items.map((item) =>
    isItemActive(item) ? applyItemStatus(item, status, now) : item,
  );
  return applyTicketStatus({ ...ticket, items }, status, now);
};

export const applySelectedItemsStatus = (
  ticket: KitchenTicket,
  itemIds: string[],
  nextStatus: KitchenTicketItemStatus,
  now: Date,
): KitchenTicket => {
  const idSet = new Set(itemIds);
  const items = ticket.items.map((item) =>
    idSet.has(item.id) && isItemActive(item)
      ? applyItemStatus(item, nextStatus, now)
      : item,
  );
  const derived = deriveTicketStatus(items);
  return applyTicketStatus({ ...ticket, items }, derived, now);
};

export const itemStatusUpdatePayload = (
  status: KitchenTicketItemStatus,
  nowIso: string,
  currentStartedAt: string | null,
): Record<string, string | null> => {
  const data: Record<string, string | null> = { status };

  if (status === 'preparing') {
    if (!currentStartedAt) {
      data.started_at = nowIso;
    }
    data.ready_at = null;
    data.delivered_at = null;
  } else if (status === 'ready') {
    data.ready_at = nowIso;
    data.delivered_at = null;
  } else if (status === 'delivered') {
    data.delivered_at = nowIso;
  }

  return data;
};

export const ticketStatusUpdatePayload = (
  status: KitchenTicketStatus,
  nowIso: string,
  currentStartedAt: string | null,
): Record<string, string | null> => {
  const data: Record<string, string | null> = {
    status,
    updated_at: nowIso,
  };

  if (status === 'preparing') {
    if (!currentStartedAt) {
      data.started_at = nowIso;
    }
    data.ready_at = null;
    data.delivered_at = null;
    data.cancelled_at = null;
  } else if (status === 'ready') {
    data.ready_at = nowIso;
    data.delivered_at = null;
    data.cancelled_at = null;
  } else if (status === 'delivered') {
    data.delivered_at = nowIso;
  } else if (status === 'cancelled') {
    data.cancelled_at = nowIso;
  }

  return data;
};
